// Helpers for extracting / synthesising the TL;DR box on blog articles.
// Pulled out of src/routes/blog.$slug.tsx so they can be unit tested in
// isolation from the route module (which pulls in the full router runtime).

export function extractTldrSection(body: string): { tldr: string; body: string } {
  const match = body.match(/^##\s+TL;?DR\s*\n+([\s\S]*?)(?=\n##\s+|$)/im);
  if (!match) return { tldr: "", body };
  const raw = match[1]
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_`>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return { tldr: raw, body: body.replace(match[0], "").trim() };
}

// Build a real summary from the article body: pull the first meaningful
// sentence from the intro plus one sentence from each major H2 section, and
// stitch them together so the TL;DR represents the whole piece rather than
// only the opening paragraph.
// Soft limit used by the verbatim guard: an authored TL;DR is treated as
// "too similar to the opening copy" when more than this fraction of its
// normalised characters also appears in the first two body paragraphs.
const VERBATIM_OVERLAP_THRESHOLD = 0.7;

export function getTldrSummary(body: string, avoidText: string = ""): string {
  const cleaned = body
    .split(/\n##\s+About the author/i)[0]
    .replace(/^#\s+.+\n+/, "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\*\*Published:\*\*[^\n]*/gi, "")
    .replace(/\*\*Last updated:\*\*[^\n]*/gi, "");

  // Prefix with a newline so a `##` heading at the very start of the body
  // still acts as a section delimiter rather than swallowing the whole article.
  const sections = `\n${cleaned}`.split(/\n##\s+[^\n]+\n/);
  const sentences: string[] = [];
  const seen = new Set<string>();

  // Above-the-fold body copy: the very first sentence of the intro. The
  // synthesised summary must not repeat it verbatim, otherwise the TL;DR is
  // just the opening line of the article re-printed above itself. We scope
  // the guard to the literal first sentence so later intro sentences remain
  // available when the article has no H2 sections to draw from.
  // Above-the-fold reference for the verbatim guard:
  // 1. The first sentence of the intro paragraph (so the TL;DR never restates
  //    the very first words a reader will see), and
  // 2. Any caller-supplied `avoidText` (e.g. a rejected authored TL;DR whose
  //    content shouldn't reappear in the synthesised fallback either).
  const introFlatForGuard = stripMarkdown(sections[0]);
  const firstIntroSentence = introFlatForGuard.match(/[^.!?]+[.!?]/)?.[0]?.trim() ?? "";
  const aboveFold = [normaliseForGuard(firstIntroSentence), normaliseForGuard(stripMarkdown(avoidText))]
    .filter(Boolean)
    .join(" ");

  const pushSentence = (raw: string) => {
    const flat = stripMarkdown(raw);
    if (!flat) return;
    const first = flat.match(/[^.!?]+[.!?]/)?.[0]?.trim() ?? flat.trim();
    if (!first || first.length < 40) return;
    // Guard: skip sentences that already appear in the above-the-fold body
    // copy. Readers will see those words anyway; the TL;DR should add value.
    if (aboveFold && aboveFold.includes(normaliseForGuard(first))) return;
    const key = first.toLowerCase().replace(/[^a-z0-9 ]/g, "").slice(0, 80);
    if (seen.has(key)) return;
    seen.add(key);
    sentences.push(first);
  };

  // Pull synthesis material from the article's section headings: each H2's
  // opening sentence acts as its thesis. We deliberately skip the intro
  // paragraphs so the TL;DR never restates the copy directly above it.
  for (const section of sections.slice(1)) {
    const firstPara = section
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .find((p) => p && !/^#{2,6}\s+/.test(p) && !/^[-*]\s+/.test(p));
    if (firstPara) pushSentence(firstPara);
    if (sentences.length >= 5) break;
  }

  // Fallback: when an article has no H2 sections (or none of them yielded a
  // usable sentence), fall back to a *later* sentence from the intro so the
  // TL;DR still appears, just from copy further down the first paragraph.
  if (sentences.length === 0) {
    const introFlat = stripMarkdown(sections[0]);
    const introSentences = introFlat.match(/[^.!?]+[.!?]/g) ?? [];
    for (const candidate of introSentences.slice(1)) {
      pushSentence(candidate);
      if (sentences.length >= 2) break;
    }
  }

  return trimToSentence(sentences.join(" "), 420);
}

// Normalise for the verbatim-overlap guard: lowercase, strip non-alphanumeric
// noise, collapse whitespace. Keeps the comparison stable across markdown
// formatting differences (e.g. bold/italics, smart quotes, trailing commas).
function normaliseForGuard(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Returns true when `candidate` is substantially a verbatim copy of any of
// the first two body paragraphs. Used to reject authored TL;DR blocks that
// were lifted directly from the article's opening copy.
export function isVerbatimOfOpening(candidate: string, body: string): boolean {
  const normalisedCandidate = normaliseForGuard(stripMarkdown(candidate));
  if (!normalisedCandidate) return false;
  const opening = normaliseForGuard(
    body
      .replace(/^#\s+.+\n+/, "")
      .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
      .split(/\n{2,}/)
      .filter((p) => p.trim() && !/^##?\s+/.test(p.trim()))
      .slice(0, 2)
      .join(" "),
  );
  if (!opening) return false;
  if (opening.includes(normalisedCandidate)) return true;
  // Partial overlap: count shared 6-word phrases. If the majority of the
  // candidate's content is reused from the opening, treat it as verbatim.
  const tokens = normalisedCandidate.split(" ").filter(Boolean);
  if (tokens.length < 6) return false;
  let overlapping = 0;
  for (let i = 0; i <= tokens.length - 6; i += 1) {
    const phrase = tokens.slice(i, i + 6).join(" ");
    if (opening.includes(phrase)) overlapping += 1;
  }
  const possible = tokens.length - 5;
  return possible > 0 && overlapping / possible >= VERBATIM_OVERLAP_THRESHOLD;
}

export function stripMarkdown(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_`>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function trimToSentence(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  const clipped = text.slice(0, maxLength);
  const sentenceEnd = Math.max(
    clipped.lastIndexOf(". "),
    clipped.lastIndexOf("? "),
    clipped.lastIndexOf("! "),
  );
  return `${clipped
    .slice(0, sentenceEnd > 160 ? sentenceEnd + 1 : clipped.lastIndexOf(" "))
    .trim()}…`;
}

// Mirrors the priority used by the article page: an authored `## TL;DR`
// section wins, otherwise we synthesise from the body, and only fall back to
// the post.description when neither produces anything meaningful.
export function selectTldr(args: {
  body: string;
  description?: string;
}): { tldr: string; body: string; source: "authored" | "synthesised" | "description" | "none" } {
  const { tldr: authored, body: bodyWithoutTldr } = extractTldrSection(args.body);
  // Use the authored TL;DR only when it isn't just a copy of the article's
  // opening paragraphs — otherwise it's redundant with the copy below it.
  if (authored && !isVerbatimOfOpening(authored, bodyWithoutTldr)) {
    return { tldr: authored, body: bodyWithoutTldr, source: "authored" };
  }
  // If we just rejected an authored TL;DR, also pass it as "avoid text" so
  // the synthesised summary doesn't rebuild the same copy from the body.
  const synth = getTldrSummary(bodyWithoutTldr, authored);
  if (synth) return { tldr: synth, body: bodyWithoutTldr, source: "synthesised" };
  if (args.description) return { tldr: args.description, body: bodyWithoutTldr, source: "description" };
  return { tldr: "", body: bodyWithoutTldr, source: "none" };
}